import React, { useState, useEffect } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { closeOutline, copyOutline, checkmarkOutline } from 'ionicons/icons';
import { useTranslation } from '../hooks/useTranslation';
import { buildPixPayload, generatePixQR } from '../services/pixService';
import './PixPaymentModal.css';

const PIX_KEY         = '+5521976400560';
const PIX_HOLDER_NAME = 'EDSON WALTER NOE VALENZUE';
const PIX_CITY        = 'RIO DE JANEIRO';

interface Props {
  eventName: string;
  amount: number;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const PixPaymentModal: React.FC<Props> = ({ eventName, amount, onConfirm, onClose }) => {
  const { t } = useTranslation();
  const [copied, setCopied]       = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [qrDataUrl, setQrDataUrl]   = useState('');
  const [pixCode, setPixCode]       = useState('');
  const [loadingQR, setLoadingQR]   = useState(true);

  useEffect(() => {
    const config = { pixKey: PIX_KEY, holderName: PIX_HOLDER_NAME, city: PIX_CITY, amount };
    const payload = buildPixPayload(config);
    setPixCode(payload);
    generatePixQR(config)
      .then(url => setQrDataUrl(url))
      .finally(() => setLoadingQR(false));
  }, [amount]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pixCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) { /* clipboard not available */ }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="pix-modal-overlay" onClick={!confirming ? onClose : undefined}>
      <div className="pix-modal" onClick={e => e.stopPropagation()}>

        <div className="pix-modal-header">
          <div className="pix-modal-header-title">
            <span className="pix-logo">🟩</span>
            <div>
              <h2>{t('pix.title')}</h2>
              <p className="pix-event-name">{eventName}</p>
            </div>
          </div>
          <button className="pix-modal-close" onClick={onClose} disabled={confirming}>
            <IonIcon icon={closeOutline} />
          </button>
        </div>

        <div className="pix-modal-body">

          {/* Monto */}
          <div className="pix-amount">
            <span className="pix-amount-label">{t('pix.amount')}</span>
            <span className="pix-amount-value">R$ {amount.toFixed(2)}</span>
          </div>

          {/* QR real */}
          <div className="pix-qr-wrap">
            {loadingQR ? (
              <div className="pix-qr-loading">
                <IonSpinner name="crescent" color="medium" />
              </div>
            ) : (
              <img src={qrDataUrl} alt="QR Pix" className="pix-qr-img" />
            )}
            <p className="pix-qr-label">{t('pix.scan_qr')}</p>
          </div>

          {/* Código copia-e-cola */}
          <div className="pix-copy-section">
            <p className="pix-copy-label">{t('pix.or_copy')}</p>
            <div className="pix-copy-row">
              <code className="pix-code">{pixCode.slice(0, 48)}...</code>
              <button className="pix-copy-btn" onClick={handleCopy} disabled={loadingQR}>
                <IonIcon icon={copied ? checkmarkOutline : copyOutline} />
                {copied ? t('pix.copied') : t('pix.copy')}
              </button>
            </div>
          </div>

          {/* Instrucciones */}
          <div className="pix-instructions">
            <p>{t('pix.instruction_1')}</p>
            <p>{t('pix.instruction_2')}</p>
          </div>

          {/* Confirm */}
          <button
            className="pix-confirm-btn"
            onClick={handleConfirm}
            disabled={confirming || loadingQR}
          >
            {confirming
              ? <><IonSpinner name="crescent" /> {t('pix.confirming')}</>
              : t('pix.confirm')
            }
          </button>

          <p className="pix-notice">{t('pix.notice')}</p>
        </div>
      </div>
    </div>
  );
};

export default PixPaymentModal;
